import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type {
  CustomerDecision,
  LineCode,
  NeedsAssessment,
  PendingResponse,
  Recommendation,
  RecommendationDetail,
} from "./types";

const recKey = (id: string) => ["recommendation", id];
// The I2 worklist key — the responded-but-unbound set. A bind (decision) must
// invalidate this so the rec drops off live (the frontend mirror of I2a's proof).
const PENDING_RESPONSES_KEY = ["recommendations", "pending-responses"];

export function useRecommendation(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: recKey(id ?? "none"),
    queryFn: () => api.get<RecommendationDetail>(`/v1/recommendations/${id}`),
    enabled: !!id,
  });
}

export interface CreateRecommendationInput {
  householdId: string;
  lineCode: LineCode;
  partyId?: string;
  insuredAssetId?: string;
  needsAssessment: NeedsAssessment;
}

export function useCreateRecommendation() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: CreateRecommendationInput) =>
      api.post<Recommendation>("/v1/recommendations", input),
  });
}

// ── GAP-AUDIT-3 D2 — the insured asset the Öneri path never sent ─────────────
// `insuredAssetId` is settable ONLY at recommendation create, and the issuance
// create schema REQUIRES it. The quote-form path sends it
// (quote-form-page.tsx:272); the Öneri form did not — so every recommendation
// started from Öneri reached DECIDED/ACCEPTED with a permanently disabled
// "Poliçeleştir" and no way back. The customer said yes and the console could
// not act on it.
//
// No new endpoint is invented to fix this. Two frozen reads compose into the
// household's asset set: GET /v1/households/:id/members then
// GET /v1/assets?partyId= per member. A household is a handful of people, so
// the fan-out is small and bounded; both reads are party:read/own and scope +
// RLS filtered server-side.

/** One insurable asset of the household, with the member who owns it. */
export interface HouseholdAsset {
  id: string;
  partyId: string;
  assetType: string;
  label: string | null;
  plate: string | null;
  dwellingAddress: string | null;
  /** Owner's display name, resolved from the members read (never a raw uuid). */
  ownerName: string;
}

interface MemberLite {
  id: string;
  firstName: string;
  lastName: string;
}
type AssetRow = Omit<HouseholdAsset, "ownerName">;

/** The asset type each line insures — mirrors catalog.line.insurable_asset_type,
 *  which the API re-enforces (assertAssetInsurable → 409 on a mismatch). Used
 *  ONLY to narrow what the picker offers; it is not an authorization decision. */
export const LINE_ASSET_TYPE: Record<string, string> = {
  trafik: "VEHICLE",
  kasko: "VEHICLE",
  dask: "DWELLING",
  konut: "DWELLING",
  tss: "PERSON",
  oss: "PERSON",
  seyahat: "PERSON",
};

/** The household's insurable assets, composed from the two frozen reads. Tolerant:
 *  a member whose assets 403/404 contributes nothing rather than failing the whole
 *  picker — the advisor still sees the assets they CAN see. */
export function useHouseholdAssets(householdId: string | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["household", householdId ?? "none", "assets"],
    enabled: enabled && !!householdId,
    retry: false,
    queryFn: async (): Promise<HouseholdAsset[]> => {
      const members = await api.get<MemberLite[]>(`/v1/households/${householdId}/members`);
      const perMember = await Promise.all(
        members.map(async (m) => {
          try {
            const page = await api.get<{ data: AssetRow[] }>(
              `/v1/assets?partyId=${encodeURIComponent(m.id)}`,
            );
            const name = `${m.firstName} ${m.lastName}`.trim();
            return (page.data ?? []).map((a) => ({ ...a, ownerName: name }));
          } catch {
            return [];
          }
        }),
      );
      // Dedupe by id — an asset could in principle surface under two members.
      const seen = new Set<string>();
      return perMember.flat().filter((a) => !seen.has(a.id) && seen.add(a.id));
    },
  });
}

/** One canonical coverage-concept — BE-1's chip vocabulary (GET /v1/catalog/coverage-concepts,
 *  catalog:read gated). `lineCode` may be null for line-agnostic concepts. The client only
 *  SELECTS from this list (it never invents concept codes); the server owns scoring. */
export interface CoverageConcept {
  code: string;
  label: string;
  lineCode: string | null;
  version: number;
  definition?: string | null;
}

/** GET /v1/catalog/coverage-concepts — the Öneri must-have/priority chip source. Gated on
 *  catalog:read (pass `enabled`); tolerant + no retry so a miss is a state, never a crash. */
export function useCoverageConcepts(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ["catalog", "coverage-concepts"],
    enabled,
    retry: false,
    queryFn: async () => {
      const res = await api.get<{ data?: CoverageConcept[] }>("/v1/catalog/coverage-concepts");
      return res?.data ?? [];
    },
  });
}

export function useScoreOffers(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (offerIds: string[]) =>
      api.post(`/v1/recommendations/${id}/offers`, {
        offers: offerIds.map((offerId) => ({ offerId })),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: recKey(id) }),
  });
}

export interface ChooseInput {
  chosenOfferId: string;
  reasonCode: string;
  cheaperButRationale?: string;
  // OPTIONAL advisor→customer note (≤300, trimmed). Sent only when non-empty; the
  // choose schema treats it as optional (persisted NULL when absent). SEPARATE from
  // the mandatory cheaperButRationale.
  advisorNote?: string;
  vulnerable?: boolean;
}

export function useChooseOffer(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChooseInput) =>
      api.post<Recommendation>(`/v1/recommendations/${id}/choose`, input),
    onSettled: () => qc.invalidateQueries({ queryKey: recKey(id) }),
  });
}

/** PENDING_TIER2 → PRESENTED, consuming the GA1 approval (x-approval-id). */
export function useTier2Review(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (approvalId: string) =>
      api.post<Recommendation>(`/v1/recommendations/${id}/tier2-review`, undefined, { approvalId }),
    onSettled: () => qc.invalidateQueries({ queryKey: recKey(id) }),
  });
}

/** GET /v1/recommendations/pending-responses — the "responses awaiting you" worklist:
 *  in-scope PRESENTED recs the customer responded to but the advisor hasn't bound
 *  (recommendation:read/own). Drops off the moment the advisor binds (see below). */
export function usePendingResponses() {
  const api = useApi();
  return useQuery({
    queryKey: PENDING_RESPONSES_KEY,
    queryFn: () => api.get<PendingResponse[]>("/v1/recommendations/pending-responses"),
  });
}

export function useRecordDecision(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { customerDecision: CustomerDecision; declineReason?: string }) =>
      api.post<Recommendation>(`/v1/recommendations/${id}/decision`, input),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: recKey(id) });
      // Binding moves the rec to DECIDED → it leaves pending-responses. Invalidate the
      // worklist so it drops off live (the frontend mirror of I2a's "bind → drops off").
      void qc.invalidateQueries({ queryKey: PENDING_RESPONSES_KEY });
    },
  });
}
